import type { InputTimeline, Player, PlayerInput } from "./types";

const WINDOW_TICKS = 128;
const integer = (n: number) => Number.isSafeInteger(n) && n >= 0;
const sequence = (n: number) => integer(n) && n <= 0xffffffff;
function sameInput(a: PlayerInput, b: PlayerInput): boolean {
  return (
    a.sequence === b.sequence &&
    a.movement.x === b.movement.x &&
    a.movement.y === b.movement.y &&
    a.aim.x === b.aim.x &&
    a.aim.y === b.aim.y &&
    a.cast.every((edge, slot) => edge === b.cast[slot])
  );
}

/** Preserve immutable admission proofs across reliable/replaced world ordering.
 * Absence from a future-only update cannot erase a command needed to replay an
 * older selected source. Only the bounded native time window retires history.
 */
export class InputJournal {
  private timelines = new Map<number, InputTimeline>();
  // Explicit departure retires human control. New bot orders are a separate
  // controller epoch; delayed human admission cannot resurrect that control.
  private retiredPlayers = new Set<number>();

  clear(): void {
    this.timelines.clear();
    this.retiredPlayers.clear();
  }

  retireBots(players: Pick<Player, "id" | "bot">[]): void {
    for (const player of players)
      if (player.bot) {
        this.retiredPlayers.add(player.id);
        if (this.timelines.get(player.id)?.controller !== "bot")
          this.timelines.delete(player.id);
      }
  }

  merge(incoming: InputTimeline[] | undefined, sourceTick: number): void {
    if (incoming === undefined) return;
    if (!Array.isArray(incoming) || incoming.length > 8)
      throw new Error("Invalid input timeline roster");
    if (!integer(sourceTick))
      throw new Error("Invalid input timeline source tick");
    const staged = new Map(this.timelines);
    const players = new Set<number>();
    for (const next of incoming) {
      if (
        !next ||
        (next.controller !== undefined &&
          next.controller !== "human" &&
          next.controller !== "bot") ||
        !sequence(next.player) ||
        !integer(next.epoch) ||
        !integer(next.controller_start_tick ?? 0) ||
        !sequence(next.base_sequence) ||
        !integer(next.base_tick) ||
        !sequence(next.resolved_sequence) ||
        !integer(next.resolved_tick) ||
        !Array.isArray(next.commands) ||
        next.commands.length > WINDOW_TICKS
      )
        throw new Error("Invalid input timeline basis");
      if (players.has(next.player))
        throw new Error("Duplicate input timeline player");
      players.add(next.player);
      if (this.retiredPlayers.has(next.player) && next.controller !== "bot")
        continue;
      const previous = staged.get(next.player);
      const botContinuation =
        previous?.controller === "bot" && next.controller === "bot";
      // A bot commitment is itself a controller barrier even when its reliable
      // packet arrives before the replaceable takeover world. Native prediction
      // still waits for a source whose roster has the matching controller.
      if (previous?.controller === "bot" && next.controller !== "bot") continue;
      if (previous && next.epoch < previous.epoch && !botContinuation) continue;
      if (
        botContinuation &&
        (previous.base_sequence !== next.base_sequence ||
          previous.base_tick !== next.base_tick ||
          (previous.controller_start_tick ?? 0) !== (next.controller_start_tick ?? 0))
      )
        throw new Error("Server changed an immutable bot input timeline basis");
      if (
        next.canceled_ticks !== undefined &&
        (!Array.isArray(next.canceled_ticks) ||
          next.canceled_ticks.length > WINDOW_TICKS * 2 + 1 ||
          next.canceled_ticks.some((tick) => !integer(tick)))
      )
        throw new Error("Invalid input cancellation history");
      const resolvedDistance = next.resolved_tick - next.base_tick;
      const predecessor =
        next.resolved_tick + 1 === next.base_tick &&
        next.resolved_sequence === (next.base_sequence - 1) >>> 0;
      const resolvedOnBasis =
        integer(resolvedDistance) &&
        resolvedDistance < 0x80000000 &&
        next.resolved_sequence ===
          (next.base_sequence + resolvedDistance) >>> 0;
      if (!predecessor && !resolvedOnBasis)
        throw new Error("Invalid native input resolution watermark");
      const prior = previous?.epoch === next.epoch ? previous : undefined;
      if (
        prior &&
        ((prior.controller ?? "human") !== (next.controller ?? "human") ||
          (prior.controller_start_tick ?? 0) !== (next.controller_start_tick ?? 0) ||
          prior.base_sequence !== next.base_sequence ||
          prior.base_tick !== next.base_tick)
      )
        throw new Error("Server changed an immutable input timeline basis");
      // Worlds can overtake multiple reliable cancellation epochs. Explicit
      // canceled slots make this union order-independent: an older packet may
      // fill a missing committed prefix but cannot revive a canceled command.
      const canceled = new Set([
        ...((botContinuation ? previous : prior)?.canceled_ticks ?? []),
        ...(next.canceled_ticks ?? []),
      ]);
      const commands = new Map(
        ((botContinuation ? previous : prior)?.commands ?? []).map(
          (command) => [command.tick, command],
        ),
      );
      for (const command of next.commands) {
        const input = command?.input;
        const distance = input && (input.sequence - next.base_sequence) >>> 0;
        if (
          !command ||
          !integer(command.tick) ||
          !input ||
          !sequence(input.sequence) ||
          distance >= 0x80000000 ||
          command.tick !== next.base_tick + distance ||
          ![
            input.movement?.x,
            input.movement?.y,
            input.aim?.x,
            input.aim?.y,
          ].every(Number.isFinite) ||
          !Array.isArray(input.cast) ||
          input.cast.length !== 4 ||
          input.cast.some((edge) => typeof edge !== "boolean")
        )
          throw new Error("Invalid native input admission");
        const old = commands.get(command.tick);
        if (old && !sameInput(old.input, input))
          throw new Error("Server changed an already admitted input");
        commands.set(command.tick, structuredClone(command));
      }
      const watermark =
        previous &&
        (botContinuation || prior) &&
        (previous.epoch > next.epoch ||
          (previous.epoch === next.epoch &&
            previous.resolved_tick > next.resolved_tick))
          ? previous
          : next;
      const floor = Math.max(0, sourceTick - WINDOW_TICKS);
      const ceiling =
        Math.max(sourceTick, watermark.resolved_tick) + WINDOW_TICKS;
      const retained = [...commands.values()]
        .filter(
          (command) =>
            command.tick >= floor &&
            command.tick <= ceiling &&
            !canceled.has(command.tick),
        )
        .sort((a, b) => a.tick - b.tick);
      if (retained.length > WINDOW_TICKS * 2 + 1)
        throw new Error("Input journal exceeded its bounded replay window");
      staged.set(next.player, {
        ...watermark,
        ...(next.canceled_ticks !== undefined ||
        previous?.canceled_ticks !== undefined
          ? {
              canceled_ticks: [...canceled]
                .filter((tick) => tick >= floor && tick <= ceiling)
                .sort((a, b) => a - b),
            }
          : {}),
        commands: retained,
      });
    }
    this.timelines = staged;
  }

  values(): InputTimeline[] {
    return [...this.timelines.values()].map((timeline) =>
      structuredClone(timeline),
    );
  }
}
